import {useEffect, useRef, useState} from "react";

/**
 * The text of a list's search box, kept apart from the search itself: `text`
 * is what is typed (bind it to the input), and `onChange` hears about it only
 * after a pause in typing, so the list isn't requested on every keystroke.
 *
 * `value` is the search the list currently has (it lives in the address). When
 * it changes from outside, say through the Back button, the box shows it.
 *
 * `onChange` runs when the search really changes (not on the first run, and
 * not when a pause leaves it as it was).
 *
 * @param {string} value the search the list currently has
 * @param {(search: string) => void} onChange
 * @param {number} [delay] milliseconds of quiet before searching
 */
export const useDebouncedSearch = (value, onChange, delay = 300) => {
    const [text, setText] = useState(value);
    // The search last handed to `onChange` or received as `value`.
    const applied = useRef(value);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        if (value !== applied.current) {
            applied.current = value;
            setText(value);
        }
    }, [value]);

    useEffect(() => {
        const timer = setTimeout(() => {
            const next = text.trim();
            if (next !== applied.current) {
                applied.current = next;
                onChangeRef.current(next);
            }
        }, delay);
        return () => clearTimeout(timer);
    }, [text, delay]);

    return {text, setText};
};
